import { Box, Button, Flex, IconButton, Switch, Table, Tbody, Td, Text, Th, Thead, Tr } from '@chakra-ui/react';
import { flexRender, getCoreRowModel, getSortedRowModel, useReactTable, createColumnHelper } from '@tanstack/react-table';
import { useMemo, useState } from 'react';
import { DeleteIcon, EditIcon } from '@chakra-ui/icons';
import isEmpty from 'lodash/isEmpty';
import { ModalType } from 'constants/common';
import { toast } from 'components/Toast';
import { useDeleteMutilShortlinkMutation } from 'services/category';
import AuthorizationComponentWrapper from 'components/AuthorizationComponentWrapper/AuthorizationComponentWrapper';
import { useUserState } from 'context/UserContext';
import { useDeleteBannerMutation, useSetActiveBannerMutation } from 'services/banner';
import { MdOutlineColorLens } from 'react-icons/md';

const SizeTable = ({ categoryData, handleUpdateCategory, refetch, onOpenPreviewModal }) => {
  const [sorting, setSorting] = useState([]);
  const columnHelper = createColumnHelper();
  const deleteBannerMutation = useDeleteBannerMutation();
  const setActiveBannerMutation = useSetActiveBannerMutation();
  const deleteMutilShortlinkMutation = useDeleteMutilShortlinkMutation();
  const { userInfo } = useUserState();
  const handleRowClick = (shortlink, type) => {
    handleUpdateCategory(shortlink, type);
  };
  const handleDeleteSize = async category => {
    const confirmDelete = window.confirm('Bạn có chắc chắn muốn xóa banner này không?');
    if (!confirmDelete) {
      return;
    }
    deleteBannerMutation.mutate(
      { id: category?._id },
      {
        onSuccess: () => {
          toast.showMessageSuccess('Xóa banner thành công');
          refetch?.();
        },
        onError: () => {
          toast.showMessageError('Xóa banner không thành công');
          refetch?.();
        },
      }
    );
  };

  const handleChange = id => {
    setActiveBannerMutation.mutate(
      { id: id },
      {
        onSuccess: () => {
          toast.showMessageSuccess('Thay đổi trạng thái banner thành công');
          refetch?.();
        },
        onError: () => {
          toast.showMessageError('Thay đổi trạng thái banner không thành công');
          refetch?.();
        },
      }
    );
  };

  const [rowSelection, setRowSelection] = useState([]);
  const onRowSelectionChange = newSelection => setRowSelection(newSelection);
  const columns = useMemo(
    () => [
      columnHelper.accessor('name', {
        header: 'Tên',
        cell: info => (
          <Box display={'flex'} alignItems={'center'} gap={3}>
            <Text>{info.row.original?.name}</Text>
          </Box>
        ),
      }),
      columnHelper.accessor('link', {
        header: 'Link',
        cell: info => (
          <Box display={'flex'} alignItems={'center'} gap={3}>
            <Text>{info.row.original?.link}</Text>
          </Box>
        ),
      }),

      columnHelper.accessor('position', {
        header: 'Position',
        cell: info => info.getValue(),
      }),
      columnHelper.accessor('isActive', {
        header: 'Active',
        cell: info => <Switch defaultChecked={info.getValue()} onChange={() => handleChange(info.row.original?._id)} />,
      }),
      columnHelper.accessor('action', {
        header: 'Thao tác',
        cell: info => (
          <AuthorizationComponentWrapper groupPermission={userInfo?.permissions} groupName={'UPDATE_PRODUCT_CATEGORY'}>
            <Flex alignItems="center" gap={1}>
              <IconButton
                bg="transparent"
                onClick={() => {
                  handleRowClick(info?.row?.original, ModalType.Add);
                }}
              >
                <EditIcon cursor="pointer" boxSize={4} />
              </IconButton>
              {/* <IconButton
              bg="transparent"
              onClick={() => {
                history.push(`/admin/category/${info?.row?.original?._id}/size`);
                }}
                >
              <TbTextResize size={16} />
              </IconButton> */}
              <IconButton
                bg="transparent"
                onClick={() => {
                  handleRowClick(info?.row?.original, ModalType.Detail);
                }}
              >
                <MdOutlineColorLens size={16} />
              </IconButton>
              <IconButton
                bg="transparent"
                onClick={() => {
                  handleDeleteSize(info?.row?.original);
                }}
              >
                <DeleteIcon color="red.400" boxSize={4} />
              </IconButton>
            </Flex>
          </AuthorizationComponentWrapper>
        ),
      }),
    ],
    [categoryData]
  );

  var table = useReactTable({
    data: categoryData || [],
    columns,
    state: {
      sorting,
      rowSelection,
    },
    onSortingChange: setSorting,
    onRowSelectionChange,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    debugTable: true,
    enableRowSelection: true,
    getRowId: row => {
      return row._id;
    },
  });

  const handleDeleteShortlink = () => {
    if (Object.keys(rowSelection)?.length === 0) {
      toast.showMessageError('Vui lòng chọn banner để xóa');
      return;
    }
    const confirmDelete = window.confirm('Bạn có chắc chắn muốn xóa các banner này không?');
    if (!confirmDelete) {
      return;
    }
    deleteMutilShortlinkMutation.mutate(
      { ids: Object.keys(rowSelection) },
      {
        onSuccess: () => {
          toast.showMessageSuccess('Xóa banner thành công');
          refetch?.();
          setRowSelection([]);
        },
        onError: () => {
          toast.showMessageError('Xóa banner không thành công');
          refetch?.();
        },
      }
    );
  };

  return (
    <>
      <Table>
        <Thead>
          {table.getHeaderGroups().map(headerGroup => (
            <Tr key={headerGroup.id}>
              {headerGroup.headers.map(header => (
                <Th key={header.id}>
                  {header.isPlaceholder ? null : (
                    <Box w={'fit-content'} cursor={header.column.getCanSort() ? 'pointer' : 'default'}>
                      {flexRender(header.column.columnDef.header, header.getContext())}
                      {/* {{
                        asc: ' 🔼',
                        desc: ' 🔽',
                      }[header.column.getIsSorted()] ?? null} */}
                    </Box>
                  )}
                </Th>
              ))}
            </Tr>
          ))}
        </Thead>
        <Tbody>
          {isEmpty(table.getRowModel().rows) ? (
            <Tr>
              <Td textAlign="center" colSpan={5}>
                Không có dữ liệu
              </Td>
            </Tr>
          ) : (
            table.getRowModel().rows.map(row => (
              <Tr key={row.id}>
                {row.getVisibleCells().map(cell => (
                  <Td key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</Td>
                ))}
              </Tr>
            ))
          )}
        </Tbody>
      </Table>
    </>
  );
};

export default SizeTable;
